import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Per-line supplier choice for a Purchase Order (backend/auto_procurement.py
// supplier_selection): a part with several suppliers linked in the BOM picks
// one of them; a part with none searches the whole Supplier Master.

const supLabel = (s) => `${s.code ? `${s.code} — ` : ""}${s.name}${s.status && s.status !== "active" ? ` (${s.status})` : ""}`;

// Dropdown of `options`; with `searchable`, a filter box narrows a long list.
export function SupplierPicker({ value, onChange, options, searchable = false, testid }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options;
    return options.filter((s) => s.id === value || supLabel(s).toLowerCase().includes(t));
  }, [q, options, value]);
  return (
    <div className="space-y-1">
      {searchable && (
        <Input className="h-8" placeholder="Search supplier code or name…" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid={testid ? `${testid}-search` : undefined} />
      )}
      <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={value || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">— Select supplier —</option>
        {shown.map((s) => <option key={s.id} value={s.id}>{supLabel(s)}</option>)}
      </select>
    </div>
  );
}

const MODE_HINT = {
  bom_multiple: "Several suppliers are linked to this part in the BOM — pick one",
  bom_conflict: "The BOM links a different supplier than the PO's — pick one",
  any: "No supplier is linked to this part in the BOM — search the Supplier Master",
  bom_single: "Only supplier linked in the BOM",
  saved: "Saved on the PO line",
};

// Shown when Send answers 409 "supplier_selection_required". `selection` is
// that answer's detail; onSaved runs after the choice is saved on the PO.
export function LineSupplierDialog({ selection, onClose, onSaved }) {
  const [picks, setPicks] = useState({});
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selection) return;
    setPicks(Object.fromEntries(selection.lines.map((r) => [r.index, r.default_supplier_id || ""])));
    if (selection.lines.some((r) => r.mode === "any")) {
      api.get("/suppliers").then(({ data }) => setAllSuppliers((data || []).filter((s) => !["blocked", "blacklisted"].includes(s.status))))
        .catch(() => setAllSuppliers([]));
    }
  }, [selection]);

  if (!selection) return null;
  const save = async () => {
    const missing = selection.lines.find((r) => r.needs_selection && !picks[r.index]);
    if (missing) return toast.error(`Select a supplier for line ${missing.index + 1} (${missing.item_code})`);
    setBusy(true);
    try {
      const lines = selection.lines.filter((r) => r.needs_selection).map((r) => ({ index: r.index, supplier_id: picks[r.index] }));
      const { data } = await api.put(`/auto-procurement/purchase-orders/${selection.po_id}/supplier-selection`, { lines });
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the supplier selection");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="po-line-supplier-dialog">
        <DialogHeader><DialogTitle>Select supplier — {selection.po_code}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          Pick the supplier for each part before the PO is sent. The choice is saved on the PO line; one PO goes to one supplier.
          {selection.po_supplier ? ` This PO is currently for ${supLabel(selection.po_supplier)}.` : ""}
        </p>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {selection.lines.map((r) => (
            <div key={r.index} className="border border-slate-200 rounded-md p-2" data-testid={`po-line-supplier-${r.index}`}>
              <div className="text-sm"><span className="mono font-semibold">{r.item_code || r.item_id}</span> {r.item_name} · qty {r.qty}</div>
              <div className="text-[11px] text-slate-500 mb-1">{MODE_HINT[r.mode]}</div>
              {r.needs_selection ? (
                <SupplierPicker value={picks[r.index]} onChange={(v) => setPicks((p) => ({ ...p, [r.index]: v }))}
                  options={r.mode === "any" ? allSuppliers : r.candidates} searchable={r.mode === "any"}
                  testid={`po-line-supplier-select-${r.index}`} />
              ) : (
                <div className="text-sm text-slate-700">
                  {supLabel(r.selected_supplier || r.candidates.find((c) => c.id === r.default_supplier_id) || { name: "—" })}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="po-line-supplier-save">
            {busy ? "Saving…" : "Save & send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const isSupplierSelectionError = (e) =>
  e?.response?.status === 409 && e.response?.data?.detail?.code === "supplier_selection_required";
